import React, { useEffect, useState } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAccessRequests, reviewAccessRequest } from '../services/api';
import './AccessRequests.css';
import './AdminPages.css';

const AccessRequests = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [approveExpiry, setApproveExpiry] = useState({}); // requestId -> date string

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchRequests();
    }
  }, [user, loading, navigate]);

  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await getAccessRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching access requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleReview = async (requestId, action, permission) => {
    try {
      const expiresAt =
        action === 'approve' && approveExpiry[requestId]
          ? approveExpiry[requestId]
          : undefined;
      const res = await reviewAccessRequest(requestId, action, permission, expiresAt);
      if (res.data.message) {
        alert(res.data.message);
      }
      setApproveExpiry((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      fetchRequests();
    } catch (error) {
      console.error('Error reviewing request:', error);
      alert(error.response?.data?.error || 'Failed to review request');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'pending') return req.status === 'pending';
    if (filter === 'approved') return req.status === 'approved';
    if (filter === 'rejected') return req.status === 'rejected';
    return true;
  });
  const requestPages = usePagination(filteredRequests, 10, filter);

  if (loading) {
    return (
      <div className="access-requests">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="access-requests">
      <div className="page-header">
        <div>
          <h1 className="page-title">Access Requests</h1>
          <p className="page-lead">Pending items need a decision before access is granted or a repository is created.</p>
        </div>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({requests.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({requests.filter(r => r.status === 'pending').length})
          </button>
          <button 
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved ({requests.filter(r => r.status === 'approved').length})
          </button>
          <button 
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected ({requests.filter(r => r.status === 'rejected').length})
          </button>
        </div>
      </div>

      <div className="requests-list">
        {loadingRequests ? (
          <div className="empty-state">
            <div className="spinner"></div>
            <p>Loading access requests…</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="empty-state">
            <p>No access requests found.</p>
          </div>
        ) : (
          requestPages.pageItems.map(request => (
            <div key={request._id} className="request-card">
              <div className="request-header">
                <div className="request-info">
                  <h3 className="request-title">
                    {request.requestKind === 'create'
                      ? `Create repository ${request.proposedRepository?.fullName || request.repository?.fullName || ''}`
                      : `Access request for ${request.repository?.fullName}`}
                  </h3>
                  <div className="request-meta">
                    <span className={`request-status status-${request.status}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                    <span className="request-date">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </span>
                    <span className="request-ticket">
                      Service desk: {request.serviceDeskRequestNumber || '—'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="request-details">
                <div className="request-detail-item">
                  <strong>Requested By:</strong>
                  <div className="user-info">
                    {request.requestedBy?.avatarUrl && (
                      <img 
                        src={request.requestedBy.avatarUrl} 
                        alt={request.requestedBy.username}
                        className="user-avatar"
                      />
                    )}
                    <span>{request.requestedBy?.username || request.requestedBy?.displayName}</span>
                  </div>
                </div>
                <div className="request-detail-item">
                  <strong>Service desk request number:</strong> {request.serviceDeskRequestNumber || '—'}
                </div>
                <div className="request-detail-item">
                  <strong>Requested For:</strong> {request.requestedFor}
                </div>
                <div className="request-detail-item">
                  <strong>Permission:</strong>
                  <span className={`permission-badge ${request.permission}`}>
                    {request.permission.charAt(0).toUpperCase() + request.permission.slice(1)}
                  </span>
                </div>
                {request.reason && (
                  <div className="request-detail-item">
                    <strong>Business justification:</strong> {request.reason}
                  </div>
                )}
                {request.requiresMultiApproval && request.approvalSteps?.length > 0 && (
                  <div className="request-detail-item approval-steps">
                    <strong>Approval workflow:</strong>
                    <ul className="approval-step-list">
                      {request.approvalSteps.map((step) => (
                        <li key={step.step} className={`approval-step approval-step--${step.status}`}>
                          Step {step.step}:{' '}
                          {step.role === 'admin' ? 'Portal admin' : 'Manager / repo owner'}
                          <span className={`step-status status-${step.status}`}>{step.status}</span>
                          {step.approvedByName ? ` — ${step.approvedByName}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!request.requiresMultiApproval && request.approvalSteps?.length > 0 && (
                  <div className="request-detail-item approval-steps">
                    <strong>Approval:</strong>{' '}
                    {request.approvalSteps[0]?.status === 'approved'
                      ? `Approved by ${request.approvalSteps[0].approvedByName || 'approver'}`
                      : 'Waiting for manager, repo owner, or admin'}
                  </div>
                )}
                {request.status === 'pending' && request.approvalHint && (
                  <div className="request-detail-item">
                    <strong>Who can approve:</strong> {request.approvalHint}
                  </div>
                )}
                {request.timeline?.length > 0 && (
                  <div className="request-detail-item">
                    <strong>Timeline:</strong>
                    <ul className="request-timeline">
                      {request.timeline.map((ev, i) => (
                        <li key={i}>
                          <span className="timeline-time">{new Date(ev.at).toLocaleString()}</span>
                          {' — '}
                          {ev.message || ev.type}
                          {ev.actorName ? ` (${ev.actorName})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {request.policiesApplied?.length > 0 && (
                  <div className="request-detail-item">
                    <strong>Policies:</strong> {request.policiesApplied.join(', ')}
                  </div>
                )}
                {request.reviewedBy && (
                  <div className="request-detail-item">
                    <strong>Reviewed By:</strong> {request.reviewedBy.username}
                    {request.reviewedAt && (
                      <span className="review-date">
                        {' '}on {new Date(request.reviewedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {request.status === 'pending' && (
                <div className="request-actions">
                  {request.canReview && (
                    <>
                      {(user?.role === 'admin' ||
                        !(
                          request.requiresMultiApproval &&
                          request.currentApprovalStep === 1 &&
                          user?.role !== 'admin'
                        )) && (
                        <div className="form-group" style={{ marginBottom: 8, maxWidth: 220 }}>
                          <label className="form-label">Access expires (optional)</label>
                          <input
                            type="date"
                            className="form-input"
                            min={new Date().toISOString().slice(0, 10)}
                            value={approveExpiry[request._id] || ''}
                            onChange={(e) =>
                              setApproveExpiry((prev) => ({
                                ...prev,
                                [request._id]: e.target.value
                              }))
                            }
                          />
                          <p className="form-hint">After expiry, access is revoked; user must request again.</p>
                        </div>
                      )}
                      <button
                        className="btn btn-primary"
                        onClick={() => handleReview(request._id, 'approve', request.permission)}
                      >
                        {request.requestKind === 'create'
                          ? 'Approve & create repository'
                          : request.requiresMultiApproval &&
                        request.currentApprovalStep === 1 &&
                        user?.role !== 'admin'
                          ? 'Approve (manager step)'
                          : 'Approve & grant access'}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReview(request._id, 'reject')}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {request.isOwnRequest && !request.canReview && (
                    <span className="request-waiting-hint">Waiting for approver review</span>
                  )}
                  {!request.isOwnRequest && !request.canReview && request.status === 'pending' && (
                    <span className="request-waiting-hint">
                      {request.currentApprovalStep === 2
                        ? 'Waiting for portal admin final approval'
                        : 'Waiting for manager or repo owner'}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {!loadingRequests && filteredRequests.length > 0 && (
        <Pagination
          page={requestPages.page}
          totalPages={requestPages.totalPages}
          total={requestPages.total}
          pageSize={requestPages.pageSize}
          onPageChange={requestPages.setPage}
          label="requests"
        />
      )}
    </div>
  );
};

export default AccessRequests;
